import React from 'react';
import { 
  ChevronRight, 
  Facebook,
  Instagram,
  Linkedin,
  MapPin,
  PhoneCall,
  Mail,
  Send,
  Plane,
  TrainFront,
  Car,
  Bus
} from 'lucide-react';
import WhatsAppIcon from './icons/WhatsAppIcon';
import { Link } from 'react-router-dom';

const Footer = () => {
  return (
    <footer className="bg-[#001a35] text-white pt-24 pb-10 relative overflow-hidden">
      {/* Animation Layer */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Flying Plane with Trail */}
        <div className="absolute top-12 left-0 animate-plane opacity-40">
          <div className="relative">
            <Plane className="text-blue-400" size={48} />
            <div className="absolute right-full top-1/2 -translate-y-1/2 w-32 h-1 bg-gradient-to-l from-blue-400/20 to-transparent rounded-full" />
          </div>
        </div>

        {/* Subhash Chandra Bose Salute - Graphical View */}
        <div className="absolute bottom-12 left-[15%] opacity-20 group">
          <svg width="120" height="180" viewBox="0 0 120 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="filter drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]">
            {/* Stylized Silhouette */}
            <path d="M40 180 L40 120 L30 110 L35 70 L25 60 L30 40 L50 30 L70 40 L75 60 L65 70 L70 110 L60 120 L60 180 Z" fill="white" fillOpacity="0.8" />
            {/* Saluting Arm */}
            <path d="M75 60 L100 40 L95 25 L75 35 Z" fill="white" fillOpacity="0.9" className="animate-pulse" />
            {/* Cap */}
            <path d="M45 35 L75 35 L70 25 L50 25 Z" fill="white" />
          </svg>
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white/10 px-3 py-1 rounded-full text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            Jai Hind
          </div>
        </div>

        {/* Floating Parashoots */}
        <div className="absolute top-24 right-[10%] animate-parashoot opacity-30">
          <div className="flex flex-col items-center">
            <div className="w-16 h-12 border-4 border-red-500/50 rounded-t-full bg-red-500/10 shadow-lg" />
            <div className="flex gap-4 -mt-1">
              <div className="w-[1px] h-8 bg-white/30 rotate-12" />
              <div className="w-[1px] h-8 bg-white/30 -rotate-12" />
            </div>
            <div className="w-4 h-4 bg-[#ffcc00] rounded-lg shadow-glow" />
          </div>
        </div>

        {/* Giant Wheel (Joinweels) */}
        <div className="absolute bottom-24 right-[5%] opacity-40 flex flex-col items-center scale-125">
          <div className="relative w-56 h-56 animate-spin-slow preserve-3d">
            <div className="absolute inset-0 border-[4px] border-dashed border-white/60 rounded-full shadow-[0_0_20px_rgba(255,255,255,0.2)]" />
            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(deg => (
              <div key={deg} className="absolute top-1/2 left-1/2 w-full h-[2px] bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-1/2 -translate-y-1/2" style={{ transform: `translate(-50%, -50%) rotate(${deg}deg)` }} />
            ))}
            {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => (
              <div key={deg} className="absolute w-6 h-6 bg-red-600 rounded-sm shadow-[0_0_10px_rgba(239,68,68,0.6)] flex items-center justify-center border border-white/20" style={{ 
                top: `${50 + 50 * Math.sin(deg * Math.PI / 180)}%`, 
                left: `${50 + 50 * Math.cos(deg * Math.PI / 180)}%`,
                transform: 'translate(-50%, -50%)'
              }}>
                <div className="w-1 h-1 bg-yellow-400 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
          <div className="w-28 h-40 bg-gradient-to-t from-slate-700 to-transparent -mt-8" style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
        </div>

        {/* Wind Turbines - Refined and Smaller */}
        <div className="absolute bottom-16 right-[22%] opacity-60 flex flex-col items-center scale-75">
          <div className="relative w-32 h-32 animate-spin-turbine">
            {[0, 120, 240].map(deg => (
              <div key={deg} className="absolute top-1/2 left-1/2 w-2 h-16 bg-gradient-to-t from-white to-slate-200 -translate-x-1/2 -translate-y-1/2 origin-top rounded-full shadow-[0_0_5px_rgba(255,255,255,0.2)]" style={{ transform: `translate(-50%, -50%) rotate(${deg}deg)` }} />
            ))}
            <div className="absolute top-1/2 left-1/2 w-3 h-3 bg-slate-400 rounded-full -translate-x-1/2 -translate-y-1/2 z-10" />
          </div>
          <div className="w-2 h-48 bg-gradient-to-b from-white/40 to-transparent -mt-16 rounded-full" />
        </div>

        <div className="absolute bottom-16 right-[38%] opacity-40 flex flex-col items-center scale-50">
          <div className="relative w-24 h-24 animate-spin-turbine [animation-delay:1s]">
            {[0, 120, 240].map(deg => (
              <div key={deg} className="absolute top-1/2 left-1/2 w-1.5 h-12 bg-white -translate-x-1/2 -translate-y-1/2 origin-top rounded-full" style={{ transform: `translate(-50%, -50%) rotate(${deg}deg)` }} />
            ))}
          </div>
          <div className="w-1.5 h-40 bg-gradient-to-b from-white/30 to-transparent -mt-12 rounded-full" />
        </div>

        <div className="absolute bottom-12 left-[10%] opacity-20 flex flex-col items-center scale-50">
          <div className="relative w-32 h-32 animate-spin-turbine [animation-delay:0.5s]">
            <div className="absolute top-1/2 left-1/2 w-2 h-16 bg-emerald-400 -translate-x-1/2 -translate-y-1/2 origin-top rounded-full" />
            <div className="absolute top-1/2 left-1/2 w-2 h-16 bg-emerald-400 -translate-x-1/2 -translate-y-1/2 origin-top rounded-full rotate-[120deg]" />
            <div className="absolute top-1/2 left-1/2 w-2 h-16 bg-emerald-400 -translate-x-1/2 -translate-y-1/2 origin-top rounded-full rotate-[240deg]" />
          </div>
          <div className="w-2 h-48 bg-gradient-to-b from-emerald-400/30 to-transparent -mt-16 rounded-full" />
        </div>

        {/* Road Objects - Cars and Bus */}
        <div className="absolute bottom-8 left-0 w-full">
          <div className="animate-road-objects [animation-delay:5s]">
            <Car size={28} className="text-amber-500 opacity-60 drop-shadow-[0_0_8px_rgba(245,158,11,0.4)]" />
          </div>
          <div className="animate-bus [animation-delay:15s]">
            <Bus size={32} className="text-blue-500 opacity-50 drop-shadow-[0_0_10px_rgba(59,130,246,0.3)]" />
          </div>
          <div className="animate-road-objects [animation-delay:2s]">
            <Car size={24} className="text-emerald-500 opacity-40" />
          </div>
        </div>

        {/* Moving Train - Right to Left and Smooth Metallic Look */}
        <div className="absolute bottom-4 left-0 w-full border-b border-white/5">
          <div className="animate-train-reverse">
            <div className="flex items-center">
              {/* Engine */}
              <div className="relative">
                <TrainFront size={48} className="text-slate-300 drop-shadow-[0_0_15px_rgba(203,213,225,0.4)] scale-x-[-1]" />
                <div className="absolute top-2 right-2 w-2.5 h-2.5 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_12px_cyan]" />
              </div>
              
              {/* Carriages */}
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="w-28 h-9 bg-gradient-to-b from-slate-200 via-slate-400 to-slate-600 mx-0.5 rounded-sm shadow-xl relative group overflow-hidden border-t border-white/30">
                  {/* Windows */}
                  <div className="absolute top-2 left-0 w-full flex justify-around px-1">
                    {[1, 2, 3, 4, 5].map(w => (
                      <div key={w} className="w-3.5 h-2 bg-cyan-100/60 rounded-[1px] shadow-[inset_0_0_4px_rgba(34,211,238,0.5)]" />
                    ))}
                  </div>
                  {/* Smooth Metallic Line */}
                  <div className="absolute bottom-1.5 left-0 w-full h-[1px] bg-cyan-300/30" />
                  {/* Reflection shine */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-shine" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="inline-block shrink-0">
                <img src="/logo.png" alt="OETechs Logo" className="h-16 md:h-20 w-auto" />
              </div>
              
              {/* Indian Flag beside logo */}
              <div className="flex items-start gap-0.5">
                <div className="w-[1.5px] h-10 bg-gradient-to-b from-slate-400 to-slate-600 rounded-full" />
                <div className="animate-flag relative">
                  <div className="w-10 h-7 flex flex-col overflow-hidden rounded-[1px] shadow-sm border border-white/10">
                    <div className="h-1/3 bg-[#FF9933]" />
                    <div className="h-1/3 bg-white flex items-center justify-center relative">
                      {/* Ashoka Chakra */}
                      <div className="w-1.5 h-1.5 border-[0.5px] border-blue-900 rounded-full flex items-center justify-center">
                        <div className="w-[0.2px] h-full bg-blue-900 absolute rotate-0" />
                        <div className="w-[0.2px] h-full bg-blue-900 absolute rotate-90" />
                        <div className="w-0.5 h-0.5 bg-blue-900 rounded-full" />
                      </div>
                    </div>
                    <div className="h-1/3 bg-[#138808]" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-black/5 via-transparent to-black/5 pointer-events-none" />
                </div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Delivering high-performance Web, Mobile & Digital Solutions to scale your business faster and smarter. <span className="text-[#ffcc00] font-semibold animate-text-glow animate__animated animate__bounce animate__infinite animate__slow inline-block">We are your digital ally, always!</span>
            </p>
            <div className="flex gap-4">
              {[
                { icon: Facebook, color: 'hover:bg-blue-600', link: 'https://www.facebook.com/share/176KPuBWFe/' },
                { icon: Instagram, color: 'hover:bg-pink-600', link: 'https://www.instagram.com/oetechs_it' },
                { icon: Linkedin, color: 'hover:bg-blue-700', link: 'https://www.linkedin.com/company/oetechs/' },
                { icon: WhatsAppIcon, color: 'hover:bg-green-500', link: 'https://whatsapp.com/channel/0029VbDBi0XFcovz5xuPCe1B', isWhatsapp: true }
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.link}
                  className={`w-10 h-10 rounded-full bg-white/5 flex items-center justify-center transition-all ${social.color} group`}
                >
                  {React.createElement(social.icon as any, { 
                    size: 18, 
                    className: "text-slate-400 group-hover:text-white transition-colors" 
                  })}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Portfolio', path: '/portfolio' },
                { name: 'Careers', path: '/careers' },
                { name: 'Team', path: '/team' },
                { name: 'Join Us', path: '/join-us' },
                { name: 'Contact', path: '/contact' }
              ].map((item) => (
                <li key={item.name}>
                  <Link to={item.path} className="text-slate-400 hover:text-[#ffcc00] text-sm transition-colors flex items-center gap-2">
                    <ChevronRight size={14} /> {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Our Services</h4>
            <ul className="space-y-4">
              {[
                { name: 'Web Development', path: '/services/web' },
                { name: 'Mobile Apps', path: '/services/mobile' },
                { name: 'UI/UX Design', path: '/services/ui-ux' },
                { name: 'Digital Marketing', path: '/services/digital-marketing' },
                { name: 'Cloud Solutions', path: '/services/cloud' },
                { name: 'Maintenance', path: '/services/maintenance' }
              ].map((item) => (
                <li key={item.name}>
                  <Link to={item.path} className="text-slate-400 hover:text-[#ffcc00] text-sm transition-colors flex items-center gap-2">
                    <ChevronRight size={14} /> {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Newsletter */}
          <div className="space-y-6">
            <h4 className="text-lg font-semibold mb-6">Get In Touch</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3 text-slate-400">
                <MapPin size={18} className="text-[#ffcc00] shrink-0 mt-1" />
                <p className="text-sm">Gachibowli, Hyderbad- 500036</p>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <PhoneCall size={18} className="text-[#ffcc00] shrink-0" />
                <div className="flex flex-col">
                  <p className="text-sm">+91 9849 231 222</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <Mail size={18} className="text-[#ffcc00] shrink-0" />
                <p className="text-sm">info@oetechs.com</p>
              </div>
            </div>
            
            <div className="pt-4">
              <div className="relative">
                <input 
                  type="email" 
                  placeholder="Your Email" 
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 pr-12 text-sm focus:outline-none focus:border-[#ffcc00] transition-colors"
                />
                <button aria-label="Subscribe" className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#ffcc00] text-[#002a5c] p-1.5 rounded-lg hover:scale-105 transition-transform">
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} OETechs Digital Solutions. All Rights Reserved.
          </p>
          <div className="flex gap-8 text-xs text-slate-500">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
